import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Centraliza o uso da Speech Synthesis API nativa do browser
 * (`window.speechSynthesis`) para leitura em voz alta das perguntas
 * do questionário (CPK-3982 — demo de acessibilidade por voz).
 *
 * Garante que apenas uma pergunta seja reproduzida por vez: cada chamada
 * a `speak` cancela qualquer leitura anterior e publica o id ativo em
 * `speaking$`, permitindo que os componentes atualizem seus ícones.
 */
@Injectable({ providedIn: 'root' })
export class SpeechSynthesisService implements OnDestroy {
  /** id da pergunta atualmente sendo lida, ou null quando em silêncio */
  private speakingId$ = new BehaviorSubject<string | null>(null);

  get speaking$(): Observable<string | null> {
    return this.speakingId$.asObservable();
  }

  /** True quando o browser suporta a Speech Synthesis API. */
  get isSupported(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  /** True quando a pergunta de id informado está sendo lida agora. */
  isSpeakingId(id: string): boolean {
    return this.speakingId$.value === id;
  }

  /**
   * Lê o texto informado em voz alta. Cancela qualquer leitura em curso
   * antes de iniciar a nova (apenas uma por vez).
   *
   * @param id   identificador da pergunta (para rastrear qual está tocando)
   * @param text texto puro a ser lido (deve corresponder ao exibido)
   * @param lang BCP-47 (ex.: 'pt-BR', 'en-US')
   */
  speak(id: string, text: string, lang: string): void {
    if (!this.isSupported) {
      return;
    }

    const clean = (text || '').trim();
    if (!clean) {
      return;
    }

    // Cancela qualquer fala anterior antes de iniciar a nova.
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(clean);
    utterance.lang = lang;

    utterance.onend = () => {
      // Só limpa se ainda somos a fala ativa (evita corrida com cancel()).
      if (this.speakingId$.value === id) {
        this.speakingId$.next(null);
      }
    };
    utterance.onerror = () => {
      if (this.speakingId$.value === id) {
        this.speakingId$.next(null);
      }
    };

    this.speakingId$.next(id);
    window.speechSynthesis.speak(utterance);
  }

  /** Interrompe qualquer leitura em andamento. */
  stop(): void {
    if (!this.isSupported) {
      return;
    }
    window.speechSynthesis.cancel();
    this.speakingId$.next(null);
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
